import {
  Link,
  mediaHelper,
  MOBILE_MAX,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { type FunctionComponent } from 'react';
import { styled } from 'styled-components';
import {
  OutboundTrackingEventData,
  trackOutboundLinkClick,
} from '../../../analytics/event-tracking/trackOutboundLinkClick';
import {
  ApiLinkVariant,
  mapApiWeblinkVariantToLinkVariant,
} from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import bannerImage from '../../../assets/mortgage-choice-banner-temporary-campaign.png';
import logoImage from '../../../assets/rea-mc-logo.svg';
import { PageContainer } from '../../../components/PageContainer/PageContainer';
import {
  ButtonFragment,
  mapDynamicContentItems,
  TextFragment,
  WebLinkFragment,
} from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';

const MORTGAGE_CHOICE_BRAND_COLOUR = '#006875';

const StyledPageContainer = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('small')};
`;

const BannerContainer = styled.div`
  display: flex;
  height: 200px;
  background: ${MORTGAGE_CHOICE_BRAND_COLOUR};
  border-radius: ${radiusHelper('large')};

  ${mediaHelper('mobile')} {
    height: 344px;
    flex-direction: column-reverse;
  }
`;

const ContentWrapper = styled.div`
  width: 50%;
  padding: ${spacingHelper('38px large')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mediaHelper('mobile')} {
    width: 100%;
    height: 50%;
    padding: ${spacingHelper('large medium')};
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacingHelper('extraSmall')};
`;

const StyledLogo = styled.img`
  width: 178px;
  height: 40px;
`;

const StyledText = styled.div`
  color: ${paletteHelper('textInvertedStatic')};
  ${typographyHelper('body01')};
  text-align: center;
  max-width: 265px;
`;

const LinkContainer = styled.div`
  padding-top: ${spacingHelper('small')};
`;

const StyledLink = styled(Link)`
  border-radius: ${radiusHelper('full')};
  color: ${MORTGAGE_CHOICE_BRAND_COLOUR};
  background-color: ${paletteHelper('backgroundPrimary')};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.25rem;
  width: 162px;

  &:hover {
    color: ${MORTGAGE_CHOICE_BRAND_COLOUR};
    background-color: ${paletteHelper('backgroundSecondaryBase')};
  }

  &:focus {
    color: ${MORTGAGE_CHOICE_BRAND_COLOUR};
    background-color: ${paletteHelper('backgroundSecondary')};
  }
`;

const ImageContainer = styled.div`
  display: flex;
  width: 50%;

  ${mediaHelper('mobile')} {
    width: 100%;
    height: 50%;
    border-top-right-radius: ${radiusHelper('large')};
    border-top-left-radius: ${radiusHelper('large')};
  }
`;

const StyledImage = styled.img`
  border-top-right-radius: ${radiusHelper('large')};
  border-bottom-right-radius: ${radiusHelper('large')};

  width: 100%;
  object-fit: cover;
  object-position: center;

  ${mediaHelper('mobile')} {
    border-top-right-radius: ${radiusHelper('large')};
    border-top-left-radius: ${radiusHelper('large')};
    border-bottom-right-radius: unset;
  }
`;

const TermsAndConditionsFullWrapper = styled.div`
  line-height: ${spacingHelper('small')};

  display: block;

  @media (max-width: ${MOBILE_MAX}px) {
    display: none;
  }
`;

const TermsAndConditionsShortWrapper = styled.span`
  display: none;

  @media (max-width: ${MOBILE_MAX}px) {
    display: block;
  }
`;

const TermsAndConditions = styled.span`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
`;

interface StreetPageBannerTemporaryCampaignProps {
  text: string;
  variant: ApiLinkVariant;
  title: string;
  url: string;
  outboundTracking: OutboundTrackingEventData;
  termsAndConditions: (ButtonFragment | TextFragment | WebLinkFragment)[] | null;
  termsAndConditionsShort: string;
}

export const StreetPageBannerTemporaryCampaign: FunctionComponent<
  StreetPageBannerTemporaryCampaignProps
> = ({
  text,
  variant,
  title,
  url,
  outboundTracking,
  termsAndConditions,
  termsAndConditionsShort,
}) => {
  const {
    schema,
    data: { event_name, type, click_url },
  } = outboundTracking;

  return (
    <StyledPageContainer>
      <BannerContainer>
        <ContentWrapper>
          <ContentContainer>
            <StyledLogo alt="Mortgage choice logo image" src={logoImage} />
            <StyledText>{text}</StyledText>
          </ContentContainer>
          <LinkContainer>
            <StyledLink
              href={url}
              variant={mapApiWeblinkVariantToLinkVariant(variant)}
              sizeVariant="medium"
              target="_blank"
              onClick={() =>
                trackOutboundLinkClick({
                  url: click_url,
                  outboundLinkType: type,
                  schema,
                  event_name,
                })
              }
            >
              {title}
            </StyledLink>
          </LinkContainer>
        </ContentWrapper>
        <ImageContainer>
          <StyledImage src={bannerImage} alt="Mortgage choice banner image" />
        </ImageContainer>
      </BannerContainer>
      <TermsAndConditionsFullWrapper>
        <TermsAndConditions
          as={DynamicContent}
          dynamicContents={mapDynamicContentItems(termsAndConditions)}
        />
      </TermsAndConditionsFullWrapper>
      <TermsAndConditionsShortWrapper>
        <TermsAndConditions>{termsAndConditionsShort}</TermsAndConditions>
      </TermsAndConditionsShortWrapper>
    </StyledPageContainer>
  );
};
